/**
 * What: "Settings" top-level page (New pages set). Extends the real Shopify Settings page
 *   (TestFeed connection, privacy, support — see ShopifySettingsContent.tsx) with a new "Store
 *   data" section that absorbs the settings/sync content formerly shown under Shopper
 *   Intelligence's Store Data tab, now that Shopper Intelligence is removed as a nav concept, and
 *   an "Integrations" entry that opens its embedded settings subpage. Usage & credits is still out of scope — it's a separate, later prompt.
 * Transport / data-flow: Pure presentation over a `SettingsLoadState`; no network, no App Bridge,
 *   The integrations catalog loads only after entering its subpage.
 *   SettingsRoute supplies the page lifecycle and real connection actions. Disconnected stores
 *   keep recovery visible without presenting old collected data as current.
 */
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { ShopSettings, StoreSyncBuildStage, StoreSyncStatus } from '@testfeed/shopify-contracts'
import type { TestFeedConnectionStatus } from '../../typesStore'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import { FooterHelp, HelpModal } from '../shared/PageHelp'
import { ConnectionSection } from './parts/ConnectionSection'
import { IntegrationsSection } from './parts/IntegrationsSection'
import { PrivacySupportSection } from './parts/PrivacySupportSection'
import { StoreDataSection } from './parts/StoreDataSection'
import type { ActionButtonLifecycle } from '../ActionButton'
import { SkeletonBodyText } from '../shims/SkeletonBodyTextShim'

const HELP_MODAL_ID = 'settings-help-modal'

export type SettingsData = {
  readiness: TestFeedConnectionStatus | null
  isLoadingReadiness: boolean
  isConnecting: boolean
  isDisconnecting: boolean
  isDisconnectUnconfirmed?: boolean
  connectionError: string | null
  planSelectionUrl?: string
  shopSettings: ShopSettings | null
  isSyncing: boolean
  buildStages: StoreSyncBuildStage[] | null
  syncStatus: StoreSyncStatus | null
}

export type SettingsLoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: SettingsData }

export interface SettingsPageProps {
  state: SettingsLoadState
  onConnect: () => void
  onDisconnect: () => void
  onRetryLoad: () => void
  shopify: ShopifyAppBridge
  isTestFeedConnected: boolean
  lifecycle?: ActionButtonLifecycle<'connect' | 'disconnect' | 'retryCleanup'>
  onRetryCleanup?: () => void
  isRefreshing?: boolean
}

function SettingsSkeleton() {
  const copy = shopifyComponentCopy.settingsIndex
  return (
    <s-page heading={copy.pageTitle} inlineSize="base">
      <div role="status" aria-label={copy.pageTitle}>
        <s-stack gap="base">
          <s-section>
            <SkeletonBodyText lines={4} />
          </s-section>
          <s-section>
            <SkeletonBodyText lines={3} />
          </s-section>
          <s-section>
            <SkeletonBodyText lines={5} />
          </s-section>
        </s-stack>
      </div>
    </s-page>
  )
}

export function SettingsPage({
  state,
  onConnect,
  onDisconnect,
  onRetryLoad,
  shopify,
  isTestFeedConnected,
  lifecycle,
  onRetryCleanup,
  isRefreshing = false,
}: SettingsPageProps) {
  const copy = shopifyComponentCopy.settingsIndex

  if (state.kind === 'loading') return <SettingsSkeleton />

  if (state.kind === 'error') {
    return (
      <s-page heading={copy.pageTitle} inlineSize="base">
        <s-banner tone="critical" heading={copy.pageErrorTitle}>
          {state.message || copy.pageErrorBody}
          <s-button slot="secondary-actions" variant="secondary" onClick={onRetryLoad} loading={isRefreshing}>
            {copy.retryAction}
          </s-button>
        </s-banner>
      </s-page>
    )
  }

  const { data } = state
  let storeData = (
    <s-section heading={copy.storeDataSectionTitle}>
      <s-text>{copy.storeDisconnected}</s-text>
    </s-section>
  )
  if (isTestFeedConnected)
    storeData = (
      <>
        <StoreDataSection
          shopSettings={data.shopSettings}
          buildStages={data.buildStages}
          isSyncing={data.isSyncing}
          syncStatus={data.syncStatus}
        />
        <IntegrationsSection shopify={shopify} isTestFeedConnected={isTestFeedConnected} />
      </>
    )

  return (
    <s-page heading={copy.pageTitle} inlineSize="base">
      <s-stack direction="block" gap="base">
        <ConnectionSection
          readiness={data.readiness}
          isLoadingReadiness={data.isLoadingReadiness}
          isConnecting={data.isConnecting}
          isDisconnecting={data.isDisconnecting}
          isDisconnectUnconfirmed={data.isDisconnectUnconfirmed}
          connectionError={data.connectionError}
          planSelectionUrl={data.planSelectionUrl}
          onConnect={onConnect}
          onDisconnect={onDisconnect}
          lifecycle={lifecycle}
          onRetryCleanup={onRetryCleanup}
        />
        <s-button onClick={onRetryLoad} loading={isRefreshing} disabled={data.isConnecting || data.isDisconnecting}>
          {copy.refreshConnection}
        </s-button>
        <PrivacySupportSection />
        {storeData}
      </s-stack>
      <FooterHelp modalId={HELP_MODAL_ID} link="how Settings works" />
      <HelpModal id={HELP_MODAL_ID} heading={copy.helpModalHeading}>
        <s-paragraph>{copy.helpModalBody1}</s-paragraph>
        <s-paragraph color="subdued">{copy.helpModalBody2}</s-paragraph>
        <s-paragraph color="subdued">{copy.helpModalBody3}</s-paragraph>
      </HelpModal>
    </s-page>
  )
}
